(() => {
  if (customElements.get('bt-product-delivery')) return;

  class BtProductDelivery extends HTMLElement {
    connectedCallback() {
      this.productRoot = this.closest('.product-information') || document;
      this.isInstantProduct = this.dataset.isInstantProduct === 'true';
      this.instant = this.querySelector('[data-bt-instant]');
      this.backorder = this.querySelector('[data-bt-backorder]');
      this.standard = this.querySelector('[data-bt-standard]');
      this.unavailable = this.querySelector('[data-bt-unavailable]');
      this.variants = this.parseVariants();
      this.handleUpdate = () => window.setTimeout(() => this.update(), 20);
      this.handleProductSelect = (event) => {
        if (!event.promise) return;
        event.promise
          .then(({ detail }) => this.update(detail?.resource?.id))
          .catch((error) => {
            if (error?.name !== 'AbortError') console.warn('[bt-product-delivery] Variant update failed:', error);
          });
      };

      this.productRoot.addEventListener('change', this.handleUpdate);
      this.productRoot.addEventListener('shopify:product:select', this.handleProductSelect);
      this.setArrivalDate();
      this.update();
    }

    disconnectedCallback() {
      this.productRoot?.removeEventListener('change', this.handleUpdate);
      this.productRoot?.removeEventListener('shopify:product:select', this.handleProductSelect);
    }

    parseVariants() {
      try {
        return JSON.parse(this.querySelector('[data-bt-delivery-variants]')?.textContent || '{}');
      } catch (_error) {
        return {};
      }
    }

    selectedVariantId(explicitVariantId) {
      if (explicitVariantId) return String(explicitVariantId);
      const input = this.productRoot.querySelector(
        'product-form input[name="id"], product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'
      );
      if (input?.value) return String(input.value);
      const urlVariantId = new URL(window.location.href).searchParams.get('variant');
      if (urlVariantId) return urlVariantId;
      return this.dataset.selectedVariantId || null;
    }

    update(explicitVariantId) {
      const variantId = this.selectedVariantId(explicitVariantId);
      const variant = this.variants[variantId];
      const isBackorder = Boolean(
        variant && variant.management !== null && Number(variant.quantity) <= 0 && variant.policy === 'continue'
      );
      const isUnavailable = Boolean(variant && variant.available === false && !isBackorder);

      this.instant.hidden = true;
      this.backorder.hidden = true;
      this.standard.hidden = true;
      this.unavailable.hidden = true;

      let deliveryMode = 'standard';
      if (isUnavailable) {
        deliveryMode = 'unavailable';
        this.unavailable.hidden = false;
      } else if (isBackorder) {
        deliveryMode = 'backorder';
        this.backorder.hidden = false;
      } else if (this.isInstantProduct) {
        deliveryMode = 'instant';
        this.instant.hidden = false;
      } else {
        this.standard.hidden = false;
      }

      if (variantId) this.dataset.selectedVariantId = variantId;
      this.syncAccordionCopy(deliveryMode);
    }

    syncAccordionCopy(deliveryMode) {
      const label = deliveryMode === 'instant'
        ? '即納商品'
        : deliveryMode === 'unavailable'
          ? '現在売り切れ'
          : 'お取り寄せ商品';
      const isInstant = deliveryMode === 'instant';
      const isNonInstant = deliveryMode === 'backorder' || deliveryMode === 'standard';
      const isUnavailable = deliveryMode === 'unavailable';

      this.productRoot.querySelectorAll('[data-bt-variant-delivery-heading]').forEach((heading) => {
        const suffix = heading.dataset.btHeadingSuffix || '';
        heading.textContent = `${label}${suffix}`;
      });
      this.productRoot.querySelectorAll('[data-bt-variant-instant-copy]').forEach((element) => {
        element.hidden = !isInstant;
      });
      this.productRoot.querySelectorAll('[data-bt-variant-backorder-copy]').forEach((element) => {
        element.hidden = !isNonInstant;
      });
      this.productRoot.querySelectorAll('[data-bt-variant-unavailable-copy]').forEach((element) => {
        element.hidden = !isUnavailable;
      });
    }

    setArrivalDate() {
      const target = this.querySelector('[data-bt-arrival-date]');
      if (!target) return;
      const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
      const shipping = new Date(now);
      const isBusinessDay = (date) => ![0, 6].includes(date.getDay());
      const addDay = (date) => {
        const next = new Date(date);
        next.setDate(next.getDate() + 1);
        return next;
      };
      const nextBusinessDay = (date) => {
        let next = new Date(date);
        do next = addDay(next); while (!isBusinessDay(next));
        return next;
      };
      const addBusinessDays = (date, days) => {
        let result = new Date(date);
        let added = 0;
        while (added < days) {
          result = addDay(result);
          if (isBusinessDay(result)) added += 1;
        }
        return result;
      };

      if (!isBusinessDay(shipping) || now.getHours() >= 10) {
        shipping.setTime(nextBusinessDay(shipping).getTime());
      }

      const labels = ['日', '月', '火', '水', '木', '金', '土'];
      const format = (date) => `${date.getMonth() + 1}月${date.getDate()}日(${labels[date.getDay()]})`;
      target.textContent = `${format(addBusinessDays(shipping, 1))}〜${format(addBusinessDays(shipping, 3))}`;
    }
  }

  customElements.define('bt-product-delivery', BtProductDelivery);

  if (!customElements.get('bt-selected-variant')) {
    class BtSelectedVariant extends HTMLElement {
      connectedCallback() {
        this.productRoot = this.closest('.product-information') || document;
        this.value = this.querySelector('[data-bt-selected-variant-value]');
        this.variants = this.parseVariants();
        this.handleChange = () => window.setTimeout(() => this.update(), 20);
        this.handleProductSelect = (event) => {
          if (!event.promise) return;
          event.promise
            .then(({ detail }) => this.update(detail?.resource?.id))
            .catch((error) => {
              if (error?.name !== 'AbortError') console.warn('[bt-selected-variant] Variant update failed:', error);
            });
        };

        this.productRoot.addEventListener('change', this.handleChange);
        this.productRoot.addEventListener('shopify:product:select', this.handleProductSelect);
        this.update();
      }

      disconnectedCallback() {
        this.productRoot?.removeEventListener('change', this.handleChange);
        this.productRoot?.removeEventListener('shopify:product:select', this.handleProductSelect);
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-bt-selected-variant-data]')?.textContent || '{}');
        } catch (_error) {
          return {};
        }
      }

      selectedVariantId(explicitVariantId) {
        if (explicitVariantId) return String(explicitVariantId);
        const input = this.productRoot.querySelector(
          'product-form input[name="id"], product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]'
        );
        return input?.value ? String(input.value) : this.dataset.selectedVariantId;
      }

      update(explicitVariantId) {
        const variantId = this.selectedVariantId(explicitVariantId);
        const variant = this.variants[variantId];
        if (!variant || !this.value) return;
        this.dataset.selectedVariantId = variantId;
        this.value.textContent = variant.title;
      }
    }

    customElements.define('bt-selected-variant', BtSelectedVariant);
  }
})();
